import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '@/i18n/useT';
import { api, type AdminCustomer } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { Card } from '@/components/ui/Card';
import { EquityCurve, DailyPnlBars, WinLossBar, CustomerBars } from '@/components/viz/Charts';

const keys = {
  overview: ['admin', 'overview'] as const,
  customers: ['admin', 'customers'] as const,
  analytics: ['admin', 'analytics'] as const,
  system: ['admin', 'system'] as const,
  activity: ['admin', 'activity'] as const,
};

const money = (n: number): string => `${n < 0 ? '-' : n > 0 ? '+' : ''}$${Math.abs(n).toFixed(2)}`;
const short = (e: string): string => e.split('@')[0];

/**
 * The operator console.
 *
 * Ordered by the questions an operator actually asks, in order: how much money
 * is this making, is the fleet healthy, which customer needs attention. The
 * headline numbers are stat tiles rather than charts because a single value is
 * not a chart; the trend gets the chart.
 */
export function AdminPage() {
  const { t } = useT();
  const { user } = useAuth();
  const qc = useQueryClient();

  const overview = useQuery({ queryKey: keys.overview, queryFn: api.adminOverview, refetchInterval: 30000, retry: false });
  const customers = useQuery({ queryKey: keys.customers, queryFn: api.adminCustomers, refetchInterval: 20000, retry: false });
  const analytics = useQuery({ queryKey: keys.analytics, queryFn: () => api.adminAnalytics(14), refetchInterval: 60000, retry: false });
  const sys = useQuery({ queryKey: keys.system, queryFn: api.adminSystem, refetchInterval: 30000, retry: false });
  const activity = useQuery({ queryKey: keys.activity, queryFn: () => api.adminActivity(40), refetchInterval: 45000, retry: false });
  const [q, setQ] = useState('');

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'start' | 'stop' | 'restart' }) => api.adminAction(id, action),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.customers });
      void qc.invalidateQueries({ queryKey: keys.overview });
    },
  });
  const suspend = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) => api.adminSuspend(id, value),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.customers }),
  });
  // Complimentary access. Keyed on the USER id, not the tenant: the grant is a
  // property of the account and outlives any bot linked to it.
  const comp = useMutation({
    mutationFn: ({ userId, grant }: { userId: string; grant: boolean }) =>
      grant
        ? api.adminGrantComplimentary(userId, t('admin.compReason'))
        : api.adminRevokeComplimentary(userId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.customers }),
  });

  if (user && user.role !== 'admin') {
    return <Card title={t('error.not_found')}><p className="muted">{t('error.unauthorized')}</p></Card>;
  }
  if (customers.isLoading) return <Card title={t('admin.title')}><p className="muted">{t('common.loading')}</p></Card>;
  if (customers.isError) return <Card title={t('admin.title')}><p className="muted">{t('error.server_error')}</p></Card>;

  const o = overview.data;
  const a = analytics.data;
  const rows: AdminCustomer[] = customers.data?.customers ?? [];
  const busy = act.isPending || suspend.isPending || comp.isPending;
  const live = new Map((a?.perCustomer ?? []).map((c) => [c.tenantId, c]));
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? rows.filter((c) => (c.user?.email ?? '').toLowerCase().includes(needle) || (c.poEmail ?? '').toLowerCase().includes(needle) || c.tenantId.toLowerCase().includes(needle))
    : rows;

  return (
    <>
      {/* Money first. A single number is a stat tile, not a chart. */}
      <section className="admin-tiles">
        <div className="admin-tile hero">
          <strong className={(a?.totals.todayNet ?? 0) >= 0 ? 'up' : 'down'}>
            {a ? money(a.totals.todayNet) : '—'}
          </strong>
          <span>{t('admin.netToday')}</span>
        </div>
        <div className="admin-tile hero">
          <strong>${a?.totals.mrrUsd ?? '—'}</strong>
          <span>{t('admin.mrr')}</span>
        </div>
        <div className="admin-tile"><strong>{o?.tenants ?? '—'}</strong><span>{t('admin.customers')}</span></div>
        <div className="admin-tile"><strong className="up">{o?.running ?? '—'}</strong><span>{t('admin.running')}</span></div>
        <div className="admin-tile"><strong className={o && o.stopped > 0 ? 'down' : ''}>{o?.stopped ?? '—'}</strong><span>{t('admin.stopped')}</span></div>
        <div className="admin-tile"><strong>{a?.totals.activeSubs ?? '—'}</strong><span>{t('admin.activeSubs')}</span></div>
      </section>

      {a ? (
        <>
          <section className="bot-card">
            <div className="bot-card-head">
              <h2>{t('admin.equityCurve')}</h2>
              <span className="muted">{t('admin.last14')}</span>
            </div>
            <EquityCurve points={a.cumulative} />
          </section>

          <section className="bot-card">
            <div className="bot-card-head">
              <h2>{t('admin.dailyPnl')}</h2>
              <span className={a.totals.net >= 0 ? 'up' : 'down'}>{money(a.totals.net)}</span>
            </div>
            <DailyPnlBars points={a.series} />
          </section>

          <section className="bot-card">
            <div className="bot-card-head">
              <h2>{t('admin.winLoss')}</h2>
              <span className="muted">{a.totals.trades} {t('admin.trades')}</span>
            </div>
            <WinLossBar wins={a.totals.wins} losses={a.totals.losses} />
          </section>

          <section className="bot-card">
            <div className="bot-card-head"><h2>{t('admin.byCustomer')}</h2></div>
            <CustomerBars rows={a.perCustomer.map((c) => ({ label: short(c.email), value: c.lifetime.net }))} />
          </section>
        </>
      ) : null}

      {/* Server health answers the operator's other standing question: can this
          box take another customer. Capacity is CPU-bound at ~2 cores per bot. */}
      {sys.data ? (
        <section className="bot-card">
          <div className="bot-card-head">
            <h2>{t('admin.system')}</h2>
            <span className="muted">{t('admin.uptime')} {Math.floor(sys.data.uptimeSec / 86400)}d</span>
          </div>
          <div className="admin-metrics">
            <div>
              <span>{t('admin.cpuLoad')}</span>
              <strong className={sys.data.loadAvg.m1 > sys.data.cores ? 'down' : ''}>
                {sys.data.loadAvg.m1.toFixed(2)} / {sys.data.cores}
              </strong>
            </div>
            <div>
              <span>{t('admin.memory')}</span>
              <strong className={sys.data.memory.pct > 85 ? 'down' : ''}>
                {(sys.data.memory.usedMb / 1024).toFixed(1)}/{(sys.data.memory.totalMb / 1024).toFixed(1)} GB
              </strong>
            </div>
            <div>
              <span>{t('admin.capacity')}</span>
              <strong className={sys.data.capacity.used >= sys.data.capacity.maxBots ? 'down' : 'up'}>
                {t('admin.slotsUsed', { used: sys.data.capacity.used, max: sys.data.capacity.maxBots })}
              </strong>
            </div>
          </div>
          {sys.data.perBot.length ? (
            <ul className="hbars" style={{ marginTop: 12 }}>
              {sys.data.perBot.map((pb) => (
                <li key={pb.tenantId}>
                  <span className="hbar-label" title={pb.tenantId}>{pb.tenantId.slice(0, 12)}</span>
                  <span className="hbar-track">
                    <span className="hbar-fill" style={{
                      width: `${Math.min(100, ((pb.memoryMb ?? 0) / 3072) * 100)}%`,
                      background: '#3987e5',
                    }} />
                  </span>
                  <span className="hbar-val">{pb.memoryMb ?? '—'} MB</span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      {/* An operator watching a launch wants to see activity happening, not a
          number that changed. */}
      <section className="bot-card">
        <div className="bot-card-head">
          <h2>{t('admin.activity')}</h2>
          <span className="muted">{activity.data?.items.length ?? 0}</span>
        </div>
        {!activity.data?.items.length ? (
          <p className="muted">{t('admin.noActivity')}</p>
        ) : (
          <ul className="bot-list">
            {activity.data.items.slice(0, 25).map((it, i) => (
              <li key={`${it.tenantId}-${it.timestamp}-${i}`} className="bot-list-row">
                <div>
                  <strong>{it.asset}</strong>
                  <span className={it.direction === 'BUY' ? 'up' : 'down'}> {it.direction}</span>
                  <div className="muted small">
                    {short(it.email)} · {new Date(it.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <div className="bot-list-right">
                  <span className={`bot-pill ${String(it.outcome).toLowerCase()}`}>{t(`status.${String(it.outcome).toLowerCase()}`)}</span>
                  <span className={it.profitUsd >= 0 ? 'up strong' : 'down strong'}>{money(it.profitUsd)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bot-card">
        <div className="bot-card-head">
          <h2>{t('admin.fleet')}</h2>
          <span className="muted">{filtered.length}</span>
        </div>
        {rows.length > 3 ? (
          <input
            className="admin-search"
            placeholder={t('admin.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        ) : null}

        {filtered.length === 0 ? (
          <p className="muted">{t('admin.noCustomers')}</p>
        ) : (
          <ul className="bot-list admin-list">
            {filtered.map((c) => {
              const l = live.get(c.tenantId);
              // The two states worth flagging: paying but not trading, and
              // trading while no longer entitled.
              const payingButStopped = c.subscription.active && !c.running;
              const runningUnpaid = c.running && !c.canRun;
              return (
                <li key={c.tenantId} className="admin-row">
                  <div className="admin-row-main">
                    <strong>{c.user?.email ?? c.tenantId}</strong>
                    <div className="muted small">{c.poEmail ?? t('common.none')}</div>

                    <div className="admin-badges">
                      <span className={`bot-pill ${c.running ? 'win' : 'cancelled'}`}>
                        {c.running ? t('dash.running') : t('dash.paused')}
                      </span>
                      <span className={`bot-pill ${c.subscription.active ? 'win' : 'loss'}`}>{c.subscription.status}</span>
                      {c.subscription.complimentary ? <span className="bot-pill win">{t('admin.complimentary')}</span> : null}
                      {c.status === 'suspended' ? <span className="bot-pill loss">{t('admin.suspended')}</span> : null}
                      {l && !l.reachable ? <span className="bot-pill loss">{t('admin.unreachable')}</span> : null}
                      {l?.halted ? <span className="bot-pill pending">{t('billing.badgeDue')}</span> : null}
                      {c.restartsPaused ? <span className="bot-pill pending">{t('admin.restartsPaused')}</span> : null}
                      {payingButStopped ? <span className="bot-pill pending">{t('admin.payingStopped')}</span> : null}
                      {runningUnpaid ? <span className="bot-pill loss">{t('admin.runningUnpaid')}</span> : null}
                    </div>

                    {/* Per-customer detail: what support actually needs to see. */}
                    {l ? (
                      <div className="admin-metrics">
                        <div><span>{t('admin.balance')}</span><strong>${l.balance ?? '—'}</strong></div>
                        <div><span>{t('admin.netToday')}</span><strong className={l.today.net >= 0 ? 'up' : 'down'}>{money(l.today.net)}</strong></div>
                        <div><span>{t('admin.trades')}</span><strong>{l.today.trades}</strong></div>
                        <div><span>{t('admin.winRate')}</span><strong>{l.today.cycleWinRate === null ? '—' : `${l.today.cycleWinRate}%`}</strong></div>
                        <div><span>{t('admin.step')}</span><strong>{l.step}</strong></div>
                        <div><span>{t('admin.lifetime')}</span><strong className={l.lifetime.net >= 0 ? 'up' : 'down'}>{money(l.lifetime.net)}</strong></div>
                      </div>
                    ) : null}

                    {l && (l.today.wins + l.today.losses) > 0 ? (
                      <WinLossBar wins={l.today.wins} losses={l.today.losses} />
                    ) : null}

                    {!c.canRun ? <div className="muted small">{c.reason}</div> : null}
                  </div>

                  <div className="admin-row-actions">
                    <button type="button" className="bot-btn tiny ghost" disabled={busy}
                      onClick={() => act.mutate({ id: c.tenantId, action: c.running ? 'stop' : 'start' })}>
                      {c.running ? t('dash.stop') : t('dash.start')}
                    </button>
                    <button type="button" className="bot-btn tiny ghost" disabled={busy}
                      onClick={() => act.mutate({ id: c.tenantId, action: 'restart' })}>
                      {t('admin.restart')}
                    </button>
                    {c.user ? (
                      <button type="button" className="bot-btn tiny ghost" disabled={busy}
                        onClick={() => comp.mutate({ userId: c.user!.id, grant: !c.subscription.complimentary })}>
                        {c.subscription.complimentary ? t('admin.revokeComp') : t('admin.grantComp')}
                      </button>
                    ) : null}
                    <button type="button" className="bot-btn tiny danger" disabled={busy}
                      onClick={() => suspend.mutate({ id: c.tenantId, value: c.status !== 'suspended' })}>
                      {c.status === 'suspended' ? t('admin.unsuspend') : t('admin.suspend')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}
